import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Download, Receipt, Trash2, Pencil } from "lucide-react";
import type { InvoiceDetailDto, InvoiceStatus } from "@procureflow/shared-types";
import { canEditDraftInvoice } from "@/lib/draft-edit-eligibility";
import { Attachments } from "@/components/attachments";
import { PageHeader } from "@/components/page-header";
import { SubmitForApprovalButton } from "@/components/submit-for-approval-button";
import { fetchWithAuth } from "@/lib/auth-client";
import { formatAmount, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/auth-context";
import { canRerunMatch } from "@/lib/match-eligibility";
import { RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// US-034 — Invoice detail page MVP. Header + summary + status badge.
// Action buttons (mark-paid / hold / unhold / approve / reject / override /
// PDF download / re-run match) land in follow-up slices alongside the
// line-items section + match-result panel + last-resolution display.

export const Route = createFileRoute("/invoices_/$id")({
  head: () => ({
    meta: [
      { title: "Invoice — ProcureFlow" },
      { name: "description", content: "Invoice detail and actions." },
    ],
  }),
  component: InvoiceDetailPage,
});

type FetchState =
  | { kind: "loading" }
  | { kind: "loaded"; data: InvoiceDetailDto }
  | { kind: "not-found" }
  | { kind: "error"; message: string };

// Invoice statuses use distinct hues so the badge communicates state at
// a glance without needing a separate label key. Matched + Paid are the
// "good" terminal states; Exception + Rejected the "bad" ones; the rest
// in-flight.
const STATUS_TONE: Record<InvoiceStatus, string> = {
  Recorded: "bg-muted text-muted-foreground border-border",
  PendingApproval: "[color:var(--warning)] bg-warning/15 border-warning/30",
  Matched: "[color:var(--success)] bg-success/15 border-success/30",
  Exception: "[color:var(--destructive)] bg-destructive/15 border-destructive/30",
  Held: "[color:var(--warning)] bg-warning/15 border-warning/30",
  Approved: "[color:var(--success)] bg-success/15 border-success/30",
  Rejected: "[color:var(--destructive)] bg-destructive/15 border-destructive/30",
  Cancelled: "bg-muted text-muted-foreground border-border",
  Paid: "[color:var(--success)] bg-success/15 border-success/30",
};

type ResolutionKind = "approve" | "reject" | "override";

function InvoiceDetailPage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<FetchState>({ kind: "loading" });
  // #3 — delete-draft confirmation Dialog state.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Mark-paid modal local state. Reference is optional; the BE / MSW
  // trims and stores empty-after-trim as null.
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [paymentReference, setPaymentReference] = useState("");
  const [markPaidError, setMarkPaidError] = useState<string | null>(null);
  const [markPaidSubmitting, setMarkPaidSubmitting] = useState(false);
  // US-043 — exception-resolution modal. Single state machine for both
  // approve + reject keeps the modal markup deduplicated.
  const [resolutionOpen, setResolutionOpen] = useState<ResolutionKind | null>(null);
  const [resolutionComment, setResolutionComment] = useState("");
  const [resolutionError, setResolutionError] = useState<string | null>(null);
  const [resolutionSubmitting, setResolutionSubmitting] = useState(false);
  // US-042 — hold + un-hold modal. Distinct from /resolution/ because
  // the comment requirement differs (hold mandatory; unhold optional)
  // and the button/label copy is meaningfully different.
  const [holdOpen, setHoldOpen] = useState<"hold" | "unhold" | null>(null);
  const [holdComment, setHoldComment] = useState("");
  const [holdError, setHoldError] = useState<string | null>(null);
  const [holdSubmitting, setHoldSubmitting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      try {
        const res = await fetchWithAuth(`/api/invoices/${id}`);
        if (cancelled) return;
        if (res.status === 404) {
          setState({ kind: "not-found" });
          return;
        }
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setState({ kind: "error", message: body.message ?? "Failed to load invoice." });
          return;
        }
        const { data } = (await res.json()) as { data: InvoiceDetailDto };
        if (!cancelled) setState({ kind: "loaded", data });
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: "error",
            message: err instanceof Error ? err.message : "Unexpected error.",
          });
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state.kind === "loading") {
    return (
      <div className="space-y-6" data-testid="invoice-detail-section">
        <PageHeader title="Invoice" description="Loading invoice details…" />
        <div className="rounded-2xl bg-card border border-border p-10 text-center text-xs text-muted-foreground shadow-elegant">
          Loading…
        </div>
      </div>
    );
  }

  if (state.kind === "not-found") {
    return (
      <div className="space-y-6" data-testid="invoice-detail-section">
        <PageHeader title="Invoice" description="We couldn't find that invoice." />
        <div className="rounded-2xl bg-card border border-border p-10 shadow-elegant">
          <p className="text-sm text-muted-foreground">
            This invoice may have been removed, or you may not have access to view it.
          </p>
          <Link
            to="/invoices/payment-queue"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to payment queue
          </Link>
        </div>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="space-y-6" data-testid="invoice-detail-section">
        <PageHeader title="Invoice" description="We couldn't load this invoice." />
        <div className="rounded-2xl bg-card border border-border p-10 text-sm text-destructive shadow-elegant">
          {state.message}
        </div>
      </div>
    );
  }

  const inv = state.data;
  const overdue = inv.status !== "Paid" && inv.status !== "Cancelled" && inv.due_date < todayIso();
  // US-046 mark-paid visibility: only Finance / Admin AND status payable.
  const canMarkPaid =
    (user?.role === "Finance" || user?.role === "Admin") &&
    (inv.status === "Matched" || inv.status === "Approved");
  // US-043 approve/reject visibility: only Finance / Admin AND status
  // ∈ {Exception, Held}.
  const canResolveException =
    (user?.role === "Finance" || user?.role === "Admin") &&
    (inv.status === "Exception" || inv.status === "Held");
  // US-042 hold/un-hold visibility: only Finance / Admin AND
  // hold-eligible (Exception → Hold) or unhold-eligible (Held → Unhold).
  const isFinanceOrAdmin = user?.role === "Finance" || user?.role === "Admin";
  // US-057: a Recorded invoice is submitted into its approval chain.
  const canSubmitForApproval =
    (user?.role === "Finance" || user?.role === "ProcurementOfficer") && inv.status === "Recorded";
  // #3: delete a draft (Recorded) invoice. DELETE releases its source GR
  // (provenance-only — no GRN counter to inverse). Roles mirror invoice create.
  const canDelete =
    inv.status === "Recorded" &&
    (user?.role === "Finance" || user?.role === "ProcurementOfficer" || user?.role === "Admin");
  // US-077: a draft (Recorded) invoice can be fully edited (header + lines, then re-match).
  const canEdit = canEditDraftInvoice(inv.status, user?.role);
  const canHold = isFinanceOrAdmin && inv.status === "Exception";
  const canUnhold = isFinanceOrAdmin && inv.status === "Held";
  // US-039 — manual match re-trigger gate. Mirrors the BE 400
  // MATCH_NOT_ELIGIBLE list (status ∈ {Recorded, Exception}).
  const canRerun = canRerunMatch(user?.role, inv.status);

  // #3 — delete this draft (Recorded) invoice. DELETE releases its source GR
  // (server-side, provenance-only); on success we leave the now-deleted detail page.
  async function handleDelete() {
    setDeleting(true);
    try {
      const res = await fetchWithAuth(`/api/invoices/${inv.id}`, { method: "DELETE" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setToast(body?.message ?? "Failed to delete invoice.");
        return;
      }
      setDeleteOpen(false);
      setToast("Invoice deleted — its source goods receipt has been released.");
      navigate({ to: "/invoices" });
    } catch {
      setToast("An unexpected error occurred.");
    } finally {
      setDeleting(false);
    }
  }

  async function handleRerunMatch() {
    try {
      const res = await fetchWithAuth(`/api/invoices/${inv.id}/match`, { method: "POST" });
      if (!res.ok) {
        setToast("Re-run match failed.");
        return;
      }
      const { data } = (await res.json()) as { data: InvoiceDetailDto };
      setState({ kind: "loaded", data });
      setToast(`Re-matched: ${data.status}`);
    } catch {
      setToast("An unexpected error occurred.");
    }
  }

  async function handleHold(kind: "hold" | "unhold") {
    setHoldError(null);
    setHoldSubmitting(true);
    try {
      const res = await fetchWithAuth(`/api/invoices/${inv.id}/${kind}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          kind === "hold" ? { comment: holdComment } : { comment: holdComment || undefined },
        ),
      });
      const body = (await res.json().catch(() => ({}))) as {
        data?: InvoiceDetailDto;
        code?: string;
        message?: string;
      };
      if (!res.ok) {
        if (body.code === "HOLD_REASON_REQUIRED") {
          setHoldError("Hold reason is required.");
          return;
        }
        if (body.code === "HOLD_NOT_ELIGIBLE" || body.code === "UNHOLD_NOT_ELIGIBLE") {
          setHoldOpen(null);
          setToast(
            kind === "hold"
              ? "This invoice cannot be placed on hold in its current status."
              : "This invoice cannot be un-held in its current status.",
          );
          return;
        }
        setHoldError(
          body.message ??
            (kind === "hold"
              ? "Hold failed. Please try again."
              : "Un-hold failed. Please try again."),
        );
        return;
      }
      if (body.data) setState({ kind: "loaded", data: body.data });
      setHoldOpen(null);
      setHoldComment("");
      setToast(kind === "hold" ? "Invoice placed on hold." : "Hold removed.");
    } catch (err) {
      setHoldError(err instanceof Error ? err.message : "Unexpected error.");
    } finally {
      setHoldSubmitting(false);
    }
  }

  async function handleResolution(kind: ResolutionKind) {
    setResolutionError(null);
    setResolutionSubmitting(true);
    try {
      const res = await fetchWithAuth(`/api/invoices/${inv.id}/${kind}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ comment: resolutionComment }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        data?: InvoiceDetailDto;
        code?: string;
        message?: string;
      };
      if (!res.ok) {
        if (
          body.code === "APPROVE_REASON_REQUIRED" ||
          body.code === "REJECT_REASON_REQUIRED" ||
          body.code === "OVERRIDE_REASON_REQUIRED"
        ) {
          setResolutionError("A reason is required.");
          return;
        }
        if (
          body.code === "APPROVE_NOT_ELIGIBLE" ||
          body.code === "REJECT_NOT_ELIGIBLE" ||
          body.code === "OVERRIDE_NOT_ELIGIBLE"
        ) {
          setResolutionOpen(null);
          const verb =
            kind === "approve" ? "approved" : kind === "reject" ? "rejected" : "overridden";
          setToast(`This invoice can no longer be ${verb} in its current status.`);
          return;
        }
        setResolutionError(body.message ?? "Resolution failed. Please try again.");
        return;
      }
      if (body.data) setState({ kind: "loaded", data: body.data });
      setResolutionOpen(null);
      setResolutionComment("");
      setToast(
        kind === "approve"
          ? "Invoice approved."
          : kind === "reject"
            ? "Invoice rejected."
            : "Override applied.",
      );
    } catch (err) {
      setResolutionError(err instanceof Error ? err.message : "Unexpected error.");
    } finally {
      setResolutionSubmitting(false);
    }
  }

  async function handleMarkPaid() {
    setMarkPaidError(null);
    setMarkPaidSubmitting(true);
    try {
      const res = await fetchWithAuth(`/api/invoices/${inv.id}/mark-paid`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payment_reference: paymentReference }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        data?: InvoiceDetailDto;
        code?: string;
        message?: string;
      };
      if (!res.ok) {
        if (body.code === "PAYMENT_REFERENCE_TOO_LONG") {
          setMarkPaidError("Payment reference must be 100 characters or fewer.");
          return;
        }
        if (body.code === "MARK_PAID_NOT_ELIGIBLE") {
          setMarkPaidOpen(false);
          setToast("This invoice cannot be marked as paid in its current status.");
          return;
        }
        setMarkPaidError(body.message ?? "Payment recording failed. Please try again.");
        return;
      }
      if (body.data) {
        setState({ kind: "loaded", data: body.data });
      }
      setMarkPaidOpen(false);
      setPaymentReference("");
      setToast("Invoice marked as paid.");
    } catch (err) {
      setMarkPaidError(err instanceof Error ? err.message : "Unexpected error.");
    } finally {
      setMarkPaidSubmitting(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="invoice-detail-section">
      <PageHeader
        title={inv.invoice_number}
        description={`Vendor invoice ${inv.vendor_invoice_number}`}
        action={
          <div className="flex items-center gap-3">
            <Link
              to="/invoices/payment-queue"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to queue
            </Link>
            <a
              href={`/api/invoices/${inv.id}/pdf`}
              download={`invoice-${inv.invoice_number}.pdf`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3.5 py-1.5 text-sm font-medium hover:bg-muted"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </a>
            {canSubmitForApproval && (
              <SubmitForApprovalButton
                endpoint={`/api/invoices/${inv.id}/submit`}
                onSubmitted={(data) => setState({ kind: "loaded", data: data as InvoiceDetailDto })}
              />
            )}
            {canHold && (
              <button
                type="button"
                onClick={() => {
                  setHoldComment("");
                  setHoldError(null);
                  setHoldOpen("hold");
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3.5 py-1.5 text-sm font-medium hover:bg-muted"
              >
                Hold Invoice
              </button>
            )}
            {canUnhold && (
              <button
                type="button"
                onClick={() => {
                  setHoldComment("");
                  setHoldError(null);
                  setHoldOpen("unhold");
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3.5 py-1.5 text-sm font-medium hover:bg-muted"
              >
                Un-hold Invoice
              </button>
            )}
            {canRerun && (
              <button
                type="button"
                onClick={() => void handleRerunMatch()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3.5 py-1.5 text-sm font-medium hover:bg-muted"
                title="POST /api/invoices/:id/match — re-runs three-way match"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Re-run match
              </button>
            )}
            {canResolveException && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setResolutionComment("");
                    setResolutionError(null);
                    setResolutionOpen("override");
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-warning/40 bg-card px-3.5 py-1.5 text-sm font-medium [color:var(--warning)] hover:bg-warning/10"
                >
                  Override
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setResolutionComment("");
                    setResolutionError(null);
                    setResolutionOpen("reject");
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/40 bg-card px-3.5 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/10"
                >
                  Reject Invoice
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setResolutionComment("");
                    setResolutionError(null);
                    setResolutionOpen("approve");
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-glow"
                >
                  Approve Invoice
                </button>
              </>
            )}
            {canMarkPaid && (
              <button
                type="button"
                onClick={() => setMarkPaidOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-glow"
              >
                Mark as Paid
              </button>
            )}
            {canEdit && (
              <Link
                to="/invoices/from-grns"
                search={{ grId: "", editId: inv.id }}
                data-testid="edit-invoice-button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3.5 py-1.5 text-sm font-medium hover:bg-muted"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Link>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                data-testid="delete-invoice-button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-destructive/40 bg-card px-3.5 py-1.5 text-sm font-medium text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-3.5 w-3.5" /> Delete
              </button>
            )}
          </div>
        }
      />

      {toast && (
        <div
          role="status"
          className="rounded-lg border border-border bg-card px-4 py-2 text-xs text-muted-foreground shadow-elegant"
        >
          {toast}
        </div>
      )}

      <ResolutionBanner invoice={inv} />

      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        <div className="p-6 border-b border-border flex items-start gap-4">
          <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center text-primary shrink-0">
            <Receipt className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-base font-semibold truncate">{inv.invoice_number}</h2>
              <span
                data-testid="invoice-status-badge"
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
                  STATUS_TONE[inv.status],
                )}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                {inv.status}
              </span>
              {overdue && (
                <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-destructive">
                  Overdue
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Vendor invoice #{inv.vendor_invoice_number} · {inv.source}
            </p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Total</p>
            <p className="text-2xl font-semibold tabular-nums">{formatAmount(inv.total_amount)}</p>
          </div>
        </div>

        <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6 p-6 text-sm">
          <FieldRow label="Vendor" value={inv.vendor.company_name} />
          <FieldRow
            label="Vendor contact"
            value={`${inv.vendor.contact_name} · ${inv.vendor.contact_email}`}
          />
          {/* US-066 — purchase_order is null for an invoice built from GRs that
              span multiple POs; show the distinct source GRs instead. */}
          {inv.purchase_order ? (
            <>
              <FieldRow
                label="Purchase order"
                value={
                  <Link
                    to="/purchase-orders/$id"
                    params={{ id: inv.purchase_order.id }}
                    className="font-mono text-xs text-primary hover:underline"
                  >
                    {inv.purchase_order.po_number}
                  </Link>
                }
              />
              <FieldRow label="PO status" value={inv.purchase_order.status} />
            </>
          ) : (
            <FieldRow
              label="Purchase orders"
              value={
                <span className="text-xs text-muted-foreground">
                  Multiple — see source receipts
                </span>
              }
            />
          )}
          {inv.source_documents.length > 0 && (
            <FieldRow
              label="Source goods receipts"
              value={
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  {inv.source_documents.map((doc, i) => (
                    <span key={doc.id} className="inline-flex items-center">
                      <Link
                        to="/grn/$id"
                        params={{ id: doc.id }}
                        className="font-mono text-xs text-primary hover:underline"
                      >
                        {doc.number}
                      </Link>
                      {i < inv.source_documents.length - 1 ? "," : ""}
                    </span>
                  ))}
                </span>
              }
            />
          )}
          <FieldRow label="Invoice date" value={formatDate(inv.invoice_date)} />
          <FieldRow
            label="Due date"
            value={
              <span className={overdue ? "text-destructive font-medium" : undefined}>
                {formatDate(inv.due_date)}
              </span>
            }
          />
          <FieldRow
            label="Source"
            value={inv.source === "VendorEmail" ? "Vendor email" : "Internal entry"}
          />
          {inv.attachment_url && (
            <FieldRow
              label="Attachment"
              value={
                <a
                  href={inv.attachment_url}
                  download={`invoice-${inv.invoice_number}-source`}
                  className="text-primary underline hover:no-underline"
                >
                  Download
                </a>
              }
            />
          )}
          {inv.paid_at && inv.paid_by ? (
            <FieldRow label="Paid" value={`${formatDate(inv.paid_at)} by ${inv.paid_by.name}`} />
          ) : (
            <FieldRow label="Paid" value={<span className="text-muted-foreground">—</span>} />
          )}
          {inv.payment_reference && (
            <FieldRow label="Payment reference" value={inv.payment_reference} />
          )}
        </dl>
      </div>

      {inv.match_result && (
        <div className="rounded-2xl bg-card border border-border p-5 shadow-elegant space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">Match result</h3>
            <span
              className={cn(
                "rounded-full border px-2 py-0.5 text-xs font-medium",
                inv.match_result.invoice_outcome === "Matched"
                  ? "[color:var(--success)] bg-success/10 border-success/30"
                  : "[color:var(--destructive)] bg-destructive/10 border-destructive/30",
              )}
            >
              {inv.match_result.invoice_outcome}
            </span>
          </div>
          {inv.match_result.line_results.length > 0 && (
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="text-left font-medium py-1">PO line</th>
                  <th className="text-right font-medium py-1">Price (BPS Δ)</th>
                  <th className="text-right font-medium py-1">Qty</th>
                  <th className="text-right font-medium py-1">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {inv.match_result.line_results.map((lr) => (
                  <tr key={lr.invoice_line_item_id} className="border-t border-border">
                    <td className="py-1.5 font-mono text-muted-foreground">
                      {lr.po_line_item_id.slice(0, 8)}…
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      <span className={lr.price_outcome === "Fail" ? "text-destructive" : ""}>
                        {lr.price_variance_bps} bps
                      </span>
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      <span className={lr.qty_outcome === "Fail" ? "text-destructive" : ""}>
                        {lr.invoice_qty}/{lr.cumulative_invoiced_qty}
                      </span>
                    </td>
                    <td className="py-1.5 text-right">
                      <span
                        className={
                          lr.line_outcome === "AutoApproved"
                            ? "[color:var(--success)]"
                            : "[color:var(--destructive)]"
                        }
                      >
                        {lr.line_outcome}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <InvoiceLineItems
        invoice={inv}
        canActOnAddons={isFinanceOrAdmin}
        onLineUpdated={(next) => setState({ kind: "loaded", data: next })}
      />

      <Dialog
        open={markPaidOpen}
        onOpenChange={(open) => {
          if (markPaidSubmitting) return;
          setMarkPaidOpen(open);
          if (!open) {
            setMarkPaidError(null);
            setPaymentReference("");
          }
        }}
      >
        <DialogContent dismissable={false}>
          <DialogHeader>
            <DialogTitle>Mark Invoice as Paid</DialogTitle>
            <DialogDescription>
              This will record the invoice as paid. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label htmlFor="payment-reference" className="text-xs font-medium text-foreground">
              Payment reference
            </label>
            <input
              id="payment-reference"
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              maxLength={100}
              placeholder="Check number, wire ID, or batch reference (optional)"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
            {markPaidError && <p className="text-xs text-destructive">{markPaidError}</p>}
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setMarkPaidOpen(false)}
              disabled={markPaidSubmitting}
              className="inline-flex items-center rounded-lg border border-border bg-background px-3.5 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleMarkPaid}
              disabled={markPaidSubmitting}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-glow disabled:opacity-50"
            >
              {markPaidSubmitting ? "Recording…" : "Confirm Payment"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* #3 — delete-draft confirmation. */}
      <Dialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (deleting) return;
          setDeleteOpen(open);
        }}
      >
        <DialogContent dismissable={false}>
          <DialogHeader>
            <DialogTitle>Delete this draft invoice?</DialogTitle>
            <DialogDescription>
              This permanently deletes the invoice and releases the goods receipt it was raised
              against, making that GR available to invoice again. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
              className="inline-flex items-center rounded-lg border border-border bg-background px-3.5 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="confirm-delete-invoice"
              className="inline-flex items-center gap-1.5 rounded-lg bg-destructive px-3.5 py-1.5 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {deleting ? "Deleting…" : "Delete Invoice"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={resolutionOpen !== null}
        onOpenChange={(open) => {
          if (resolutionSubmitting) return;
          if (!open) {
            setResolutionOpen(null);
            setResolutionComment("");
            setResolutionError(null);
          }
        }}
      >
        <DialogContent dismissable={false}>
          <DialogHeader>
            <DialogTitle>
              {resolutionOpen === "approve"
                ? "Approve Invoice"
                : resolutionOpen === "reject"
                  ? "Reject Invoice"
                  : "Override Matching"}
            </DialogTitle>
            <DialogDescription>
              {resolutionOpen === "approve"
                ? "This will approve the invoice and route it to the payment queue."
                : resolutionOpen === "reject"
                  ? "This will permanently reject the invoice."
                  : "This bypasses matching rules and authorises payment for all outstanding line failures. Use only when you accept full responsibility for the discrepancies."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label htmlFor="resolution-reason" className="text-xs font-medium text-foreground">
              {resolutionOpen === "approve"
                ? "Reason for approval"
                : resolutionOpen === "reject"
                  ? "Reason for rejection"
                  : "Business justification for override"}
            </label>
            <textarea
              id="resolution-reason"
              value={resolutionComment}
              onChange={(e) => setResolutionComment(e.target.value)}
              rows={3}
              placeholder={
                resolutionOpen === "approve"
                  ? "Why is this exception OK to approve?"
                  : resolutionOpen === "reject"
                    ? "Why is this invoice being rejected?"
                    : "Document the business justification for bypassing matching."
              }
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
            {resolutionError && <p className="text-xs text-destructive">{resolutionError}</p>}
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setResolutionOpen(null)}
              disabled={resolutionSubmitting}
              className="inline-flex items-center rounded-lg border border-border bg-background px-3.5 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => resolutionOpen && handleResolution(resolutionOpen)}
              disabled={resolutionSubmitting || resolutionComment.trim() === ""}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-medium shadow-glow disabled:opacity-50",
                resolutionOpen === "reject"
                  ? "bg-destructive text-destructive-foreground"
                  : resolutionOpen === "override"
                    ? "[background:var(--warning)] text-background"
                    : "bg-gradient-primary text-primary-foreground",
              )}
            >
              {resolutionSubmitting
                ? "Submitting…"
                : resolutionOpen === "approve"
                  ? "Confirm Approval"
                  : resolutionOpen === "reject"
                    ? "Confirm Rejection"
                    : "Confirm Override"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={holdOpen !== null}
        onOpenChange={(open) => {
          if (holdSubmitting) return;
          if (!open) {
            setHoldOpen(null);
            setHoldComment("");
            setHoldError(null);
          }
        }}
      >
        <DialogContent dismissable={false}>
          <DialogHeader>
            <DialogTitle>
              {holdOpen === "hold" ? "Place Invoice on Hold" : "Remove Hold"}
            </DialogTitle>
            <DialogDescription>
              {holdOpen === "hold"
                ? "This invoice will be removed from the exceptions queue until un-held."
                : "This invoice will return to the exceptions queue."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label htmlFor="hold-reason" className="text-xs font-medium text-foreground">
              {holdOpen === "hold" ? "Reason for hold" : "Reason for removing hold (optional)"}
            </label>
            <textarea
              id="hold-reason"
              value={holdComment}
              onChange={(e) => setHoldComment(e.target.value)}
              rows={3}
              placeholder={
                holdOpen === "hold"
                  ? "Why is this invoice being held?"
                  : "Why is the hold being removed?"
              }
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
            {holdError && <p className="text-xs text-destructive">{holdError}</p>}
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setHoldOpen(null)}
              disabled={holdSubmitting}
              className="inline-flex items-center rounded-lg border border-border bg-background px-3.5 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => holdOpen && handleHold(holdOpen)}
              disabled={holdSubmitting || (holdOpen === "hold" && holdComment.trim() === "")}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3.5 py-1.5 text-sm font-medium text-primary-foreground shadow-glow disabled:opacity-50"
            >
              {holdSubmitting ? "Submitting…" : "Confirm"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Attachments ownerType="invoices" ownerId={id} />
    </div>
  );
}

// US-044 — Terminal-resolution banner. Mutually exclusive across the
// three event types; absent until a resolution lands (status remains
// Recorded / Matched / Exception / Held / etc.). Visible to every role
// with detail access — resolution context is intentionally shared.
function ResolutionBanner({ invoice }: { invoice: InvoiceDetailDto }) {
  const lr = invoice.last_resolution;
  if (!lr.event_type) return null;
  const actorName = lr.actor?.name ?? "(unknown)";
  const occurred = lr.occurred_at ? formatDate(lr.occurred_at) : "";
  const comment = lr.comment ?? "";

  let tone: string;
  let label: string;
  let body: string;
  if (lr.event_type === "INVOICE_EXCEPTION_APPROVED" && invoice.status === "Approved") {
    tone = "[color:var(--success)] bg-success/10 border-success/30";
    label = "Exception Approved";
    body = `Approved by ${actorName} on ${occurred}. Reason: ${comment}`;
  } else if (lr.event_type === "INVOICE_EXCEPTION_REJECTED" && invoice.status === "Rejected") {
    tone = "[color:var(--destructive)] bg-destructive/10 border-destructive/30";
    label = "Invoice Rejected";
    body = `Rejected by ${actorName} on ${occurred}. Reason: ${comment}`;
  } else if (lr.event_type === "MATCH_MANUAL_OVERRIDE" && invoice.status === "Approved") {
    tone = "[color:var(--warning)] bg-warning/10 border-warning/30";
    label = "Manual Override";
    body = `Overridden by ${actorName} on ${occurred}. Reason: ${comment}`;
  } else {
    // Stale resolution (e.g. status transitioned away without a new
    // resolution event) — defensively skip rather than misrender.
    return null;
  }

  return (
    <div
      data-testid="resolution-banner"
      className={cn("rounded-2xl border px-5 py-4 shadow-elegant", tone)}
    >
      <p className="text-xs font-semibold uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-sm">{body}</p>
    </div>
  );
}

// US-045 — Invoice line items section with per-row AddOn controls.
// PO lines render read-only; AddOn lines expose Approve + Reject actions
// while addon_status is Pending. The Reject path prompts for a reason
// via a row-scoped inline input — keeps the slice atomic by reusing
// the same row affordance instead of opening another modal.
function InvoiceLineItems({
  invoice,
  canActOnAddons,
  onLineUpdated,
}: {
  invoice: InvoiceDetailDto;
  canActOnAddons: boolean;
  onLineUpdated: (next: InvoiceDetailDto) => void;
}) {
  return (
    <div
      data-testid="invoice-line-items"
      className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden"
    >
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <h2 className="text-sm font-semibold">Line Items</h2>
        <span className="text-xs text-muted-foreground">
          {invoice.line_items.length} line{invoice.line_items.length === 1 ? "" : "s"}
        </span>
      </div>
      {invoice.line_items.length === 0 ? (
        <div className="px-5 py-8 text-xs text-muted-foreground text-center">
          No line items recorded.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-xs text-muted-foreground">
            <tr>
              <th className="px-5 py-2.5 text-left font-medium">Description</th>
              <th className="px-3 py-2.5 text-left font-medium">Type</th>
              <th className="px-3 py-2.5 text-right font-medium">Qty</th>
              <th className="px-3 py-2.5 text-right font-medium">Unit price</th>
              <th className="px-3 py-2.5 text-right font-medium">Line total</th>
              <th className="px-3 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoice.line_items.map((line) => (
              <LineRow
                key={line.id}
                invoiceId={invoice.id}
                line={line}
                canActOnAddons={canActOnAddons}
                onUpdated={onLineUpdated}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function LineRow({
  invoiceId,
  line,
  canActOnAddons,
  onUpdated,
}: {
  invoiceId: string;
  line: InvoiceDetailDto["line_items"][number];
  canActOnAddons: boolean;
  onUpdated: (next: InvoiceDetailDto) => void;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function callAction(kind: "approve" | "reject") {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`/api/invoices/${invoiceId}/line-items/${line.id}/${kind}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(kind === "reject" ? { comment: reason } : { comment: "" }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        data?: InvoiceDetailDto;
        code?: string;
        message?: string;
      };
      if (!res.ok) {
        setError(body.message ?? "Action failed.");
        return;
      }
      if (body.data) onUpdated(body.data);
      if (kind === "reject") {
        setRejecting(false);
        setReason("");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const isPendingAddon = line.line_type === "AddOn" && line.addon_status === "Pending";

  return (
    <tr data-testid={`line-row-${line.id}`} className="border-t border-border align-top">
      <td className="px-5 py-3">
        <div>{line.description}</div>
        {line.po_line_description && (
          <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
            PO line: {line.po_line_description}
          </div>
        )}
        {/* US-066 — per-line provenance to the source goods receipt. */}
        {line.source_gr_number && (
          <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
            Source GR: {line.source_gr_number}
          </div>
        )}
      </td>
      <td className="px-3 py-3 text-xs">
        {line.line_type === "AddOn" ? (
          <span
            className={cn(
              "inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              line.addon_status === "Pending" && "bg-warning/15 [color:var(--warning)]",
              line.addon_status === "Approved" && "bg-success/15 [color:var(--success)]",
              line.addon_status === "Rejected" && "bg-destructive/15 text-destructive",
              !line.addon_status && "bg-muted text-muted-foreground",
            )}
          >
            Add-on{line.addon_status ? ` · ${line.addon_status}` : ""}
          </span>
        ) : (
          <span className="text-muted-foreground">PO</span>
        )}
      </td>
      <td className="px-3 py-3 text-right tabular-nums">{line.quantity}</td>
      <td className="px-3 py-3 text-right tabular-nums">{formatAmount(line.unit_price)}</td>
      <td className="px-3 py-3 text-right tabular-nums font-medium">
        {formatAmount(line.line_total)}
      </td>
      <td className="px-3 py-3 text-right">
        {isPendingAddon && canActOnAddons && !rejecting && (
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => callAction("approve")}
              disabled={submitting}
              className="rounded-md border border-border bg-card px-2 py-1 text-xs font-medium hover:bg-muted disabled:opacity-50"
            >
              Approve
            </button>
            <button
              type="button"
              onClick={() => {
                setRejecting(true);
                setReason("");
                setError(null);
              }}
              disabled={submitting}
              className="rounded-md border border-destructive/40 bg-card px-2 py-1 text-xs font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        )}
        {isPendingAddon && canActOnAddons && rejecting && (
          <div className="inline-flex items-center gap-1">
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason"
              className="w-32 rounded-md border border-border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
            />
            <button
              type="button"
              onClick={() => callAction("reject")}
              disabled={submitting || reason.trim() === ""}
              className="rounded-md bg-destructive px-2 py-1 text-xs font-medium text-destructive-foreground disabled:opacity-50"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => {
                setRejecting(false);
                setReason("");
              }}
              disabled={submitting}
              className="rounded-md border border-border bg-card px-2 py-1 text-xs font-medium hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        )}
        {error && <p className="mt-1 text-[11px] text-destructive">{error}</p>}
      </td>
    </tr>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words">{value}</dd>
    </div>
  );
}

function todayIso(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate(),
  ).padStart(2, "0")}`;
}
